import { useState, useEffect, useRef, Component } from 'react';
import { formatDate, stripHtml } from '../utils';
import useLazyImage from '../hooks/useLazyImage';
import ExportMenu from './ExportMenu';

// ─── ERROR BOUNDARY ───────────────────────────────────────────────────────────
// Wraps the SVG canvas so a single bad relationship configuration (e.g. a
// cycle, an orphaned ref, or a render-time exception we didn't anticipate)
// surfaces as an inline warning instead of blanking the whole tree.
class TreeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, message: (err && err.message) || 'Unknown render error' };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[TreeTimeline] render error:', err, info);
  }
  // Reset when the events array reference changes so the user can recover by
  // editing the bad relationship.
  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, message: '' });
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="tree-error-fallback" role="alert">
          <strong>Tree could not render.</strong>
          <span>One or more relationships could not be drawn. Check for conflicting connections (cycles, self-references, or relationships that point to deleted events).</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── TREE TIMELINE COMPONENT ───────────────────────────────────────────────────
//
//  Renders events as a branching tree structure. Events with no parentEventId
//  appear on the main trunk; events with parentEventId branch off their parent.
//
//  Props:
//    events       {array}    event objects (may include parentEventId, branchLabel,
//                            branchColor, collapsed fields)
//    colors       {object}   { primary, secondary, tertiary }
//    onEventClick {function} called with the event object
//    manualOrder  {boolean}  unused — tree order is by date/parent
//

// ── Constants ──────────────────────────────────────────────────────────────────

const LEVEL_GAP  = 220;   // px between generation levels
const NODE_SEP   = 78;    // min spacing between sibling nodes
const PADDING    = 90;    // canvas padding
// Uniform node radii so every event circle (and the profile photo it
// frames) is the same size regardless of tier. We previously used 20/15/11
// for root/branch/leaf — that produced visibly different photo sizes,
// which read as inconsistent in genealogy-style trees.
const NR         = { root: 16, branch: 16, leaf: 16 };
const MS_H       = 13;    // milestone diamond half-size
const TRUNK_W    = 5;
const PREVIEW_W  = 270;

const PALETTE = [
  '#4A8FD4','#D96B3B','#5AA35C','#9059A8','#D4A83B',
  '#1A9E8C','#C94444','#5B8FD4','#3FAF70','#C04A9A',
];

// ── Tree building ──────────────────────────────────────────────────────────────

const buildTree = (events) => {
  const nm = {};
  events.forEach(ev => { nm[ev.id] = { ev, children: [], depth: 0 }; });

  // Walk the parent chain to detect cycles before wiring children.  If a
  // node's parentEventId chains back to itself (e.g. someone picked
  // themselves in the Biological Parent 1 dropdown for a same-named event),
  // we treat the node as parentless rather than letting it become
  // unreachable from any root — otherwise it silently disappears from the
  // tree because addVis only descends from roots.
  const validParentId = (startId) => {
    const start = nm[startId];
    if (!start) return null;
    let pid = start.ev.parentEventId;
    const seen = new Set([startId]);
    while (pid) {
      if (seen.has(pid)) return null; // cycle → ignore the link
      if (!nm[pid]) return null;      // orphan ref → ignore
      seen.add(pid);
      pid = nm[pid].ev.parentEventId;
    }
    return start.ev.parentEventId; // first link is safe
  };

  const roots = [];
  // Spouse-only nodes: no parentEventId but at least one resolvable spouse.
  // They sit at the same level as their spouse via the spousal connector
  // instead of attaching to a vertical branch (e.g. a person who married
  // into the family from an external bloodline).
  const spouseOnly = [];
  events.forEach(ev => {
    const pid = validParentId(ev.id);
    if (pid && nm[pid]) {
      nm[pid].children.push(ev.id);
      return;
    }
    const spouses = Array.isArray(ev.spouseEventIds) ? ev.spouseEventIds : [];
    const hasValidSpouse = spouses.some(sid => sid && sid !== ev.id && nm[sid]);
    if (hasValidSpouse) spouseOnly.push(ev.id);
    else roots.push(ev.id);
  });

  const byDate = (a, b) => ((nm[a].ev.startDate || "").localeCompare(nm[b].ev.startDate || ""));
  roots.sort(byDate);
  Object.values(nm).forEach(n => n.children.sort(byDate));

  // If every event ended up spouse-only (a mutually-spouse couple with no
  // parent on either side, plus their descendants), nothing seeds the
  // traversal — the visibility pass produces an empty set and the canvas
  // goes blank.  Promote spouse-only nodes that own a subtree (i.e. have
  // children) so the walk seeds from the actual trunk, not from a leaf
  // partner.  Multiple promotions are allowed for trees that have several
  // unrelated couples; the partners of promoted nodes remain spouse-only
  // and attach via the spouse-offset placement as usual.  Falls back to
  // the first spouse-only entry if nobody has children — in that edge
  // case the tree is just a couple with no descendants and any seed works.
  if (roots.length === 0 && spouseOnly.length > 0) {
    const withKids = spouseOnly.filter(id => (nm[id]?.children?.length ?? 0) > 0);
    const toPromote = withKids.length > 0 ? withKids : [spouseOnly[0]];
    toPromote.forEach(id => {
      const idx = spouseOnly.indexOf(id);
      if (idx >= 0) spouseOnly.splice(idx, 1);
      roots.push(id);
    });
  }

  const setDepth = (id, d) => { nm[id].depth = d; nm[id].children.forEach(c => setDepth(c, d + 1)); };
  roots.forEach(id => setDepth(id, 0));
  // Spouse-only nodes inherit depth from their primary spouse so they line
  // up on the same horizontal/vertical level when the tree is rendered.
  // Their CHILDREN need depth propagated too — otherwise a tree whose
  // primary parent is a spouse-only node (e.g. the mother set as
  // Biological Parent 1) leaves descendants stuck at depth 0 with
  // undefined positions.
  spouseOnly.forEach(id => {
    const sids = nm[id].ev.spouseEventIds || [];
    const sid = sids.find(s => nm[s]);
    const d = sid ? nm[sid].depth : 0;
    nm[id].depth = d;
    nm[id].children.forEach(cId => setDepth(cId, d + 1));
  });

  return { nm, roots, spouseOnly };
};

// ── Branch color assignment ────────────────────────────────────────────────────

const buildColors = (nm, roots) => {
  const out = {};
  let pi = 0;
  const walk = (id, inh) => {
    const color = nm[id].ev.branchColor || inh;
    out[id] = color;
    nm[id].children.forEach(c => walk(c, color));
  };
  roots.forEach(id => walk(id, PALETTE[pi++ % PALETTE.length]));
  return out;
};

// ── Layout (simplified Reingold-Tilford) ──────────────────────────────────────

const computeLayout = (nm, roots, spouseOnly, orient, collapsed) => {
  let leaf = 0;

  const assignPos = (id) => {
    const vis = collapsed.has(id) ? [] : nm[id].children;
    if (!vis.length) { nm[id]._p = leaf++; return; }
    vis.forEach(c => assignPos(c));
    const f = nm[vis[0]]._p, l = nm[vis[vis.length - 1]]._p;
    nm[id]._p = (f + l) / 2;
  };
  roots.forEach(id => assignPos(id));
  // Walk subtrees rooted at spouse-only nodes too — otherwise their
  // descendants never get _p assigned and render at NaN coordinates
  // (effectively invisible).  The spouse-only node's own _p ends up
  // set here but is overridden by the spouse-offset placement below;
  // only its children's _p actually matters for downstream layout.
  spouseOnly.forEach(id => {
    if (nm[id]._p === undefined) assignPos(id);
  });

  const pos = {};
  Object.keys(nm).forEach(id => {
    const n = nm[id];
    // Skip spouse-only nodes here — they're placed below relative to spouse.
    // (We can't compute their position from `n._p` since they weren't
    // included in the assignPos walk.)
    if (spouseOnly && spouseOnly.includes(id)) return;
    pos[id] = orient === 'tb'
      ? { x: n._p * NODE_SEP + PADDING, y: n.depth * LEVEL_GAP + PADDING }
      : { x: n.depth * LEVEL_GAP + PADDING, y: n._p * NODE_SEP + PADDING };
  });

  // Place spouse-only nodes adjacent to their primary spouse on the axis
  // perpendicular to the hierarchy direction.  Multiple spouse-only
  // partners for the same spouse fan out, alternating sides so they
  // don't overlap.
  //
  // Spouse offset uses HALF the sibling spacing (NODE_SEP * 0.5) so a
  // spouse can never land on the same coordinate as one of the partner's
  // siblings — siblings always occupy ±NODE_SEP slots relative to each
  // other.  After computing the candidate position, run a final pass that
  // bumps any remaining collision outward in 0.5-NODE_SEP increments
  // until the slot is clear.
  if (spouseOnly && spouseOnly.length) {
    const slotForSpouse = {}; // spouseId → number of partners already placed
    const SPOUSE_GAP = NODE_SEP * 0.5;
    const samePoint = (a, b) => Math.abs(a.x - b.x) < 4 && Math.abs(a.y - b.y) < 4;
    const occupied = (cand) => Object.values(pos).some(q => samePoint(q, cand));
    spouseOnly.forEach(id => {
      const sids = nm[id].ev.spouseEventIds || [];
      const sid = sids.find(s => pos[s]);
      if (!sid) {
        pos[id] = { x: PADDING, y: PADDING };
        return;
      }
      const slot   = (slotForSpouse[sid] || 0) + 1;
      slotForSpouse[sid] = slot;
      const side   = slot % 2 === 0 ? -1 : 1;
      const anchor = pos[sid];

      let candidate;
      let bump = Math.ceil(slot / 2); // 1, 1, 2, 2, 3, 3...
      // Try the half-step slot first; if a sibling already occupies that
      // exact spot, push outward until we find empty space.
      for (let safety = 0; safety < 12; safety++) {
        const dist = SPOUSE_GAP * bump;
        candidate = orient === 'tb'
          ? { x: anchor.x + side * dist, y: anchor.y }
          : { x: anchor.x, y: anchor.y + side * dist };
        if (!occupied(candidate)) break;
        bump += 1;
      }
      pos[id] = candidate;
    });
  }
  return pos;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const getSubtree = (id, nm) => {
  const s = new Set([id]);
  const w = (n) => nm[n].children.forEach(c => { s.add(c); w(c); });
  w(id);
  return s;
};

const getAncestors = (id, nm) => {
  const s = new Set();
  let c = nm[id]?.ev.parentEventId;
  while (c && nm[c]) { s.add(c); c = nm[c].ev.parentEventId; }
  return s;
};

const countDesc = (id, nm) =>
  nm[id].children.reduce((s, c) => s + 1 + countDesc(c, nm), 0);

const lw = (depth) => Math.max(1, TRUNK_W - depth * 0.65);

// ─────────────────────────────────────────────────────────────────────────────

// Sub-component so useLazyImage can be called legally outside a .map() loop.
const TreeNodeImage = ({ src, x, y, size, clipId }) => {
  const imgRef = useLazyImage(src);
  return (
    <image
      ref={imgRef}
      x={x} y={y}
      width={size} height={size}
      clipPath={`url(#${clipId})`}
      preserveAspectRatio="xMidYMid slice"
      style={{ pointerEvents: 'none' }}
    />
  );
};

const TreeTimeline = ({ events, colors, onEventClick, timeline = null, isEmbed = false }) => {
  const [orient,      setOrient]      = useState('lr');
  const [collapsed,   setCollapsed]   = useState(new Set());
  const [selected,    setSelected]    = useState(null);
  const [hovered,     setHovered]     = useState(null);
  const [hoverScrPos, setHoverScrPos] = useState({ x: 0, y: 0 });
  const [isolated,    setIsolated]    = useState(null);
  const [search,      setSearch]      = useState('');
  const [matchIdx,    setMatchIdx]    = useState(0);
  const [zoom,        setZoom]        = useState(1);
  const [pan,         setPan]         = useState({ x: 0, y: 0 });
  const [legendCollapsed, setLegendCollapsed] = useState(false);

  // Hover preview hide is debounced so the cursor can travel from the
  // SVG node onto the floating preview card without the card vanishing
  // mid-transit.  See onMouseLeave on the node and onMouseEnter on the
  // preview below.
  const hideHoverTimerRef = useRef(null);
  const scheduleHoverHide = () => {
    if (hideHoverTimerRef.current) clearTimeout(hideHoverTimerRef.current);
    // Bumped from 180ms to 450ms so the cursor has plenty of time to
    // cross the gap from the node to the preview card before the hide
    // fires.  The card's own onMouseEnter cancels this timer.
    hideHoverTimerRef.current = setTimeout(() => setHovered(null), 450);
  };
  const cancelHoverHide = () => {
    if (hideHoverTimerRef.current) { clearTimeout(hideHoverTimerRef.current); hideHoverTimerRef.current = null; }
  };

  const containerRef = useRef(null);
  const isPanning    = useRef(false);
  const panStart     = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const posRef       = useRef({});
  const zoomRef      = useRef(1);
  const panRef       = useRef(pan);
  const visIdsRef    = useRef(new Set());
  const nmRef        = useRef({});

  // ── Tree data ────────────────────────────────────────────────────────────────
  // Computed once per render; any exception is caught and we fall back to an
  // empty tree + a console warning. The downstream ErrorBoundary will catch
  // anything that slips past these synchronous guards during the JSX render.
  let nm, roots, spouseOnly, branchColors, positions;
  let layoutError = null;
  try {
    ({ nm, roots, spouseOnly } = buildTree(events));
    branchColors = buildColors(nm, roots);
    positions    = computeLayout(nm, roots, spouseOnly, orient, collapsed);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[TreeTimeline] layout error:', err);
    nm = {}; roots = []; spouseOnly = []; branchColors = {}; positions = {};
    layoutError = (err && err.message) || 'Layout failed';
  }
  posRef.current         = positions;
  zoomRef.current        = zoom;
  panRef.current         = pan;
  nmRef.current          = nm;

  // Visible nodes — defensive against missing nm entries / cyclic children
  // lists so a malformed relationship can never silently blank the canvas.
  const visIds = new Set();
  const visiting = new Set();
  const addVis = (id) => {
    if (!id || visiting.has(id)) return; // guard against cyclic children
    if (!nm[id]) return;                  // guard against orphan refs
    if (isolated && !getSubtree(isolated, nm).has(id)) return;
    visiting.add(id);
    visIds.add(id);
    if (!collapsed.has(id) && Array.isArray(nm[id].children)) {
      nm[id].children.forEach(c => addVis(c));
    }
    visiting.delete(id);
  };
  try {
    if (isolated && nm[isolated]) addVis(isolated);
    else roots.forEach(id => addVis(id));

    // Spouse-only nodes piggy-back on their spouse's visibility.  Run as a
    // fixed-point loop with a hard sweep cap so a malformed graph can't
    // spin forever.
    let added = true;
    let sweeps = 0;
    while (added && sweeps < spouseOnly.length + 1) {
      added = false;
      sweeps++;
      spouseOnly.forEach(id => {
        if (visIds.has(id) || !nm[id]) return;
        const sids = nm[id].ev?.spouseEventIds || [];
        if (sids.some(s => visIds.has(s))) {
          addVis(id);
          added = true;
        }
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[TreeTimeline] visibility pass error:', err);
    layoutError = (err && err.message) || 'Visibility pass failed';
  }
  // If events exist but nothing's visible we have a silent failure mode
  // — surface a warning so the user isn't stuck staring at a blank canvas.
  if (!layoutError && events && events.length > 0 && visIds.size === 0) {
    layoutError = 'No nodes could be placed. Check your relationship configuration for cycles or missing parents.';
  }
  visIdsRef.current      = visIds;
  const spouseOnlySet    = new Set(spouseOnly);

  // Canvas bounds
  let maxX = 500, maxY = 400;
  visIds.forEach(id => {
    const p = positions[id];
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  });
  const canvasW = maxX + 200;
  const canvasH = maxY + 120;

  // Search matches
  const sq = search.toLowerCase().trim();
  const matches = sq
    ? [...visIds].filter(id => nm[id].ev.title.toLowerCase().includes(sq))
    : [];
  const curMatch = matches[matchIdx] || null;

  // Highlight path on hover
  const highlightEdges = new Set();
  if (hovered) {
    let c = hovered;
    while (c && nm[c]?.ev.parentEventId) {
      const p = nm[c].ev.parentEventId;
      if (p) highlightEdges.add(`${p}|${c}`);
      c = p;
    }
  }

  // Roots on screen for trunk
  const visRoots = roots.filter(id => visIds.has(id));

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const onMove = (e) => {
      if (!isPanning.current) return;
      setPan({ x: panStart.current.px + e.clientX - panStart.current.mx,
               y: panStart.current.py + e.clientY - panStart.current.my });
    };
    const onUp = () => { isPanning.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(z => Math.min(3, Math.max(0.15, z * (e.deltaY < 0 ? 1.12 : 0.89))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Auto-pan to search match
  useEffect(() => {
    if (!matches.length) return;
    const p = posRef.current[matches[matchIdx]];
    if (!p || !containerRef.current) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    setPan({ x: width / 2 - p.x * zoomRef.current, y: height / 2 - p.y * zoomRef.current });
  }, [matchIdx, matches.join(',')]); // eslint-disable-line

  // ── Touch: two-finger pan + pinch zoom + double-tap focus + long-press collapse
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.touchAction = 'none';

    let t1Start         = null;  // { x, y, t } single-finger start
    let twoStart        = null;  // { midX, midY, panX, panY, pinchDist }
    let lastTapTime     = 0;
    let lastTapX        = 0;
    let lastTapY        = 0;
    let longPressTimer  = null;
    let moved           = false;
    let prevPinchScale  = 1;

    const dist2  = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const midPt  = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });
    const clearLP = () => { if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; } };

    const screenToSvg = (sx, sy) => {
      const rect = el.getBoundingClientRect();
      return {
        x: ((sx - rect.left) - panRef.current.x) / zoomRef.current,
        y: ((sy - rect.top)  - panRef.current.y) / zoomRef.current,
      };
    };

    const nearestNode = (svgX, svgY, filter, maxDist = 80) => {
      let best = null, bestD = Infinity;
      for (const id of visIdsRef.current) {
        if (filter && !filter(id)) continue;
        const p = posRef.current[id];
        if (!p) continue;
        const d = Math.hypot(svgX - p.x, svgY - p.y);
        if (d < bestD && d < maxDist) { bestD = d; best = id; }
      }
      return best;
    };

    const onStart = (e) => {
      moved = false;
      clearLP();

      if (e.touches.length >= 2) {
        t1Start = null;
        const m = midPt(e.touches[0], e.touches[1]);
        const d = dist2(e.touches[0], e.touches[1]);
        twoStart = { midX: m.x, midY: m.y, panX: panRef.current.x, panY: panRef.current.y, pinchDist: d };
        prevPinchScale = 1;
        return;
      }

      twoStart = null;
      if (e.touches.length === 1) {
        const t = e.touches[0];
        t1Start = { x: t.clientX, y: t.clientY, t: Date.now() };
        const tx = t.clientX, ty = t.clientY;
        longPressTimer = setTimeout(() => {
          if (moved) return;
          longPressTimer = null;
          const { x: svgX, y: svgY } = screenToSvg(tx, ty);
          const id = nearestNode(svgX, svgY, (id) => nmRef.current[id]?.children.length > 0, 60);
          if (id) {
            setCollapsed(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
          }
        }, 500);
      }
    };

    const onMove = (e) => {
      if (e.touches.length >= 2 && twoStart) {
        e.preventDefault();
        const m = midPt(e.touches[0], e.touches[1]);
        setPan({ x: twoStart.panX + m.x - twoStart.midX, y: twoStart.panY + m.y - twoStart.midY });
        const d     = dist2(e.touches[0], e.touches[1]);
        const scale = d / twoStart.pinchDist;
        const dScale = scale / prevPinchScale;
        if (dScale > 0.5 && dScale < 2) {
          setZoom(z => Math.max(0.15, Math.min(3, z * dScale)));
        }
        prevPinchScale = scale;
        return;
      }

      if (t1Start && e.touches.length === 1) {
        const dx = Math.abs(e.touches[0].clientX - t1Start.x);
        const dy = Math.abs(e.touches[0].clientY - t1Start.y);
        if (dx > 8 || dy > 8) { moved = true; clearLP(); }
      }
    };

    const onEnd = (e) => {
      clearLP();
      twoStart = null;
      if (!t1Start || moved || e.changedTouches.length === 0) { t1Start = null; return; }

      const t = e.changedTouches[0];
      const now = Date.now();
      if (now - lastTapTime < 300 && Math.hypot(t.clientX - lastTapX, t.clientY - lastTapY) < 40) {
        // Double tap → zoom into nearest node
        const { x: svgX, y: svgY } = screenToSvg(t.clientX, t.clientY);
        const id = nearestNode(svgX, svgY, null, 120);
        if (id) {
          const p = posRef.current[id];
          const rect = el.getBoundingClientRect();
          const targetZoom = Math.min(3, Math.max(1, zoomRef.current * 1.5));
          setPan({ x: rect.width / 2 - p.x * targetZoom, y: rect.height / 2 - p.y * targetZoom });
          setZoom(targetZoom);
        }
        lastTapTime = 0;
      } else {
        lastTapTime = now;
        lastTapX = t.clientX;
        lastTapY = t.clientY;
      }
      t1Start = null;
    };

    const onCancel = () => { clearLP(); t1Start = null; twoStart = null; };

    el.addEventListener('touchstart',  onStart,  { passive: true });
    el.addEventListener('touchmove',   onMove,   { passive: false });
    el.addEventListener('touchend',    onEnd,    { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });

    return () => {
      el.style.touchAction = '';
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onCancel);
      clearLP();
    };
  }, []); // all dynamic values accessed via refs

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleNodeClick = (id, e) => {
    e.stopPropagation();
    setSelected(s => s === id ? null : id);
  };

  const toggleCollapse = (id, e) => {
    e.stopPropagation();
    setCollapsed(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const fitToScreen = () => {
    const el = containerRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setZoom(Math.min(1, width / canvasW, height / canvasH) * 0.88);
    setPan({ x: 0, y: 0 });
  };

  const handleKeyDown = (e) => {
    if (!selected || !nm[selected]) return;
    const node = nm[selected];
    const pid = node.ev.parentEventId;
    const siblings = pid && nm[pid] ? nm[pid].children : roots;
    const si = siblings.indexOf(selected);
    const visChildren = collapsed.has(selected) ? [] : node.children;

    const go = (id) => { if (id) { e.preventDefault(); setSelected(id); } };

    if (orient === 'lr') {
      if (e.key === 'ArrowRight') go(visChildren[0]);
      else if (e.key === 'ArrowLeft') go(pid);
      else if (e.key === 'ArrowUp' && si > 0) go(siblings[si - 1]);
      else if (e.key === 'ArrowDown' && si < siblings.length - 1) go(siblings[si + 1]);
    } else {
      if (e.key === 'ArrowDown') go(visChildren[0]);
      else if (e.key === 'ArrowUp') go(pid);
      else if (e.key === 'ArrowLeft' && si > 0) go(siblings[si - 1]);
      else if (e.key === 'ArrowRight' && si < siblings.length - 1) go(siblings[si + 1]);
    }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick(node.ev); }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (!events.length) return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
      No events yet. Add your first event.
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="tree-root"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >

      {/* ── Controls bar ── */}
      <div className="tree-ctrl-bar">
        <div className="tree-ctrl-group">
          {[['lr', 'Left → Right'], ['tb', 'Top ↓ Bottom']].map(([o, lbl]) => (
            <button
              key={o}
              className={`tree-ctrl-btn${orient === o ? ' tree-ctrl-active' : ''}`}
              onClick={() => setOrient(o)}
              style={orient === o ? { borderColor: colors.primary, color: colors.primary, background: `${colors.primary}15` } : {}}
            >
              {lbl}
            </button>
          ))}
        </div>

        <div className="tree-ctrl-group">
          <button className="tree-ctrl-btn" onClick={() => {
            const ids = Object.keys(nm).filter(id => nm[id].children.length > 0);
            setCollapsed(new Set(ids));
          }}>Collapse All</button>
          <button className="tree-ctrl-btn" onClick={() => setCollapsed(new Set())}>Expand All</button>
          {isolated && (
            <button
              className="tree-ctrl-btn"
              style={{ color: colors.primary, borderColor: colors.primary }}
              onClick={() => setIsolated(null)}
            >
              ✕ Show All
            </button>
          )}
        </div>

        <div className="tree-ctrl-group">
          <button className="tree-ctrl-btn" onClick={() => setZoom(z => Math.min(3, z * 1.2))} title="Zoom in">＋</button>
          <button className="tree-ctrl-btn" onClick={() => setZoom(z => Math.max(0.15, z * 0.82))} title="Zoom out">－</button>
          <button className="tree-ctrl-btn" onClick={fitToScreen} title="Fit to screen">⊡</button>
        </div>

        <div className="tree-ctrl-search">
          <input
            className="tree-search-input"
            value={search}
            onChange={e => { setSearch(e.target.value); setMatchIdx(0); }}
            placeholder="Search nodes…"
          />
          {matches.length > 0 && (
            <>
              <span className="tree-search-count">{matchIdx + 1}/{matches.length}</span>
              <button className="tree-ctrl-btn tree-ctrl-sm" onClick={() => setMatchIdx(i => (i - 1 + matches.length) % matches.length)}>‹</button>
              <button className="tree-ctrl-btn tree-ctrl-sm" onClick={() => setMatchIdx(i => (i + 1) % matches.length)}>›</button>
            </>
          )}
        </div>

        {!isEmbed && timeline && (
          <ExportMenu timeline={timeline} captureRef={containerRef} />
        )}
      </div>

      {/* ── Pan/zoom viewport ── */}
      <div
        className="tree-viewport"
        onMouseDown={e => {
          if (e.button !== 0) return;
          isPanning.current = true;
          panStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
        }}
        onClick={() => setSelected(null)}
      >
        <TreeErrorBoundary resetKey={events?.length ?? 0}>
        {layoutError && (
          <div className="tree-error-fallback" role="alert">
            <strong>Layout warning.</strong>
            <span>One or more relationships could not be rendered. Check for conflicting connections.</span>
          </div>
        )}
        <div
          className="tree-canvas-wrap"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', width: canvasW, height: canvasH }}
        >
          <svg width={canvasW} height={canvasH} className="tree-svg">

            {/* Trunk — connects all visible root nodes */}
            {visRoots.length > 1 && (() => {
              const first = positions[visRoots[0]];
              const last  = positions[visRoots[visRoots.length - 1]];
              return (
                <line
                  x1={first.x} y1={first.y}
                  x2={last.x}  y2={last.y}
                  stroke={colors.primary}
                  strokeWidth={TRUNK_W}
                  strokeLinecap="round"
                  opacity={0.7}
                />
              );
            })()}

            {/* Branch edges.  Two passes: first gather every visible
                parent→child edge and bucket the ones that use a wishbone
                (both parents known) by their sorted parent-pair, then
                emit a single wishbone group per bucket so multiple
                siblings sharing the same two parents converge on one
                shared junction. Single-parent edges render as before. */}
            {(() => {
              const wishboneGroups = {}; // key "p1|p2" (sorted) → { p1Id, p2Id, children: [{cId, isHot}] }
              const singleEdges    = []; // { parentId, childId, isHot }

              visIds.forEach(id => {
                if (collapsed.has(id)) return;
                nm[id].children.forEach(cId => {
                  if (!visIds.has(cId)) return;
                  const childEv = nm[cId].ev;
                  const p2Id    = childEv.secondParentEventId;
                  const edgeKey = `${id}|${cId}`;
                  const isHot   = highlightEdges.has(edgeKey);
                  const useWishbone = p2Id && p2Id !== id && nm[p2Id] && visIds.has(p2Id) && positions[p2Id];
                  if (useWishbone) {
                    const pairKey = id < p2Id ? `${id}|${p2Id}` : `${p2Id}|${id}`;
                    if (!wishboneGroups[pairKey]) {
                      wishboneGroups[pairKey] = { p1Id: id, p2Id, children: [] };
                    }
                    wishboneGroups[pairKey].children.push({ cId, isHot });
                  } else {
                    singleEdges.push({ parentId: id, childId: cId, isHot });
                  }
                });
              });

              const singleNodes = singleEdges.map(({ parentId, childId, isHot }) => {
                const p   = positions[parentId];
                const c   = positions[childId];
                // Drop the edge if either endpoint is missing or non-finite.
                if (!p || !c || !Number.isFinite(p.x) || !Number.isFinite(c.x)) return null;
                const col = branchColors[childId] || colors.secondary;
                const lineW = lw(nm[parentId]?.depth ?? 0);
                let d;
                if (orient === 'lr') {
                  const dx = (c.x - p.x) * 0.44;
                  d = `M ${p.x} ${p.y} C ${p.x + dx} ${p.y} ${c.x - dx} ${c.y} ${c.x} ${c.y}`;
                } else {
                  const dy = (c.y - p.y) * 0.44;
                  d = `M ${p.x} ${p.y} C ${p.x} ${p.y + dy} ${c.x} ${c.y - dy} ${c.x} ${c.y}`;
                }
                return (
                  <path
                    key={`se|${parentId}|${childId}`}
                    d={d}
                    stroke={col}
                    strokeWidth={isHot ? lineW + 2 : lineW}
                    fill="none"
                    strokeLinecap="round"
                    opacity={isHot ? 1 : 0.7}
                    className="tree-edge"
                  />
                );
              });

              const wishboneNodes = Object.entries(wishboneGroups).map(([pairKey, group]) => {
                const p1 = positions[group.p1Id];
                const p2 = positions[group.p2Id];
                if (!p1 || !p2 || !Number.isFinite(p1.x) || !Number.isFinite(p2.x)) return null;
                const jx = (p1.x + p2.x) / 2;
                const jy = (p1.y + p2.y) / 2;
                // Pick a width/colour from the deeper parent so the line
                // weight matches the surrounding branch tier.
                const lineW = Math.max(
                  lw(nm[group.p1Id]?.depth ?? 0),
                  lw(nm[group.p2Id]?.depth ?? 0)
                );
                // Branch colour comes from any child in the group so it
                // tracks the branchColor cascade.
                const branchCol = branchColors[group.children[0]?.cId] || colors.secondary;
                const anyHot = group.children.some(c => c.isHot);
                const op = anyHot ? 1 : 0.7;
                return (
                  <g key={`wb|${pairKey}`} className="tree-wishbone">
                    {/* Two arms from each parent meet at the shared junction */}
                    <line x1={p1.x} y1={p1.y} x2={jx} y2={jy}
                          stroke={branchCol} strokeWidth={lineW} opacity={op}
                          strokeLinecap="round" className="tree-edge" />
                    <line x1={p2.x} y1={p2.y} x2={jx} y2={jy}
                          stroke={branchCol} strokeWidth={lineW} opacity={op}
                          strokeLinecap="round" className="tree-edge" />
                    {/* One arm from the junction to each sibling — every
                        child in the group reaches the same junction. */}
                    {group.children.map(({ cId, isHot }) => {
                      const c   = positions[cId];
                      if (!c || !Number.isFinite(c.x)) return null;
                      const col = branchColors[cId] || colors.secondary;
                      const w   = isHot ? lineW + 2 : lineW;
                      return (
                        <line
                          key={`wbc|${pairKey}|${cId}`}
                          x1={jx} y1={jy} x2={c.x} y2={c.y}
                          stroke={col}
                          strokeWidth={w}
                          opacity={isHot ? 1 : 0.7}
                          strokeLinecap="round"
                          className="tree-edge"
                        />
                      );
                    })}
                    <circle cx={jx} cy={jy} r={4} fill="#C9A96E" />
                  </g>
                );
              });

              return [...singleNodes, ...wishboneNodes];
            })()}

            {/* Adoption connectors (dotted gold line + scroll icon).
                Drawn before spouse connectors so the spouse line/heart sits
                on top when an edge happens to overlap.  Pairs are deduped
                using sorted-id keys (an adopted person may list multiple
                adopters; both directions are honoured for safety). */}
            {(() => {
              const drawn = new Set();
              const adoptionLines = [];
              const considerPair = (childId, parentId) => {
                if (!childId || !parentId || childId === parentId) return;
                if (!visIds.has(childId) || !visIds.has(parentId)) return;
                if (!nm[childId] || !nm[parentId]) return;
                const key = childId < parentId ? `${childId}|${parentId}` : `${parentId}|${childId}`;
                if (drawn.has(key)) return;
                drawn.add(key);
                let aId = parentId, bId = childId;
                if (positions[aId].x > positions[bId].x) { aId = childId; bId = parentId; }
                const a = positions[aId];
                const b = positions[bId];
                const radiusFor = (nodeId) => {
                  const n = nm[nodeId];
                  if (!n) return NR.branch;
                  const isSpouseOnly = spouseOnlySet.has(nodeId);
                  const isRoot = !n.ev.parentEventId && !isSpouseOnly;
                  const isLeaf = n.children.length === 0;
                  return isRoot ? NR.root : isLeaf ? NR.leaf : NR.branch;
                };
                const ra = radiusFor(aId);
                const rb = radiusFor(bId);
                const x1 = a.x + ra, y1 = a.y, x2 = b.x - rb, y2 = b.y;
                const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
                adoptionLines.push(
                  <g key={`ad|${key}`} className="tree-adoption" data-pair={key}>
                    <line
                      x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke="#C9A96E"
                      strokeWidth={2}
                      strokeDasharray="3 5"
                      strokeLinecap="round"
                      opacity={0.6}
                      className="tree-adoption-line"
                    />
                    {/* Scroll icon (12×12). Hover scales to 1.33×. */}
                    <g transform={`translate(${mx - 6} ${my - 6})`} className="tree-adoption-scroll">
                      <path
                        d="M2.2 0.9 L9.8 0.9 C 10.6 0.9 11.2 1.6 11.2 2.5 L 11.2 9.5 C 11.2 10.4 10.6 11.1 9.8 11.1 L 2.2 11.1 C 1.4 11.1 0.8 10.4 0.8 9.5 L 0.8 2.5 C 0.8 1.6 1.4 0.9 2.2 0.9 Z M 2.5 3.6 L 9.5 3.6 M 2.5 5.4 L 9.5 5.4 M 2.5 7.2 L 7.5 7.2"
                        fill="#C9A96E"
                        stroke="#C9A96E"
                        strokeWidth="0.5"
                      />
                    </g>
                  </g>
                );
              };
              visIds.forEach(id => {
                // Each event may list its own adoptive parents…
                const adopters = nm[id]?.ev?.adoptedByEventIds;
                if (Array.isArray(adopters)) {
                  adopters.forEach(parentId => considerPair(id, parentId));
                }
              });
              return adoptionLines;
            })()}

            {/* Spousal connectors (dashed gold line + heart at midpoint).
                Drawn after branch edges and before nodes so the heart icon
                sits visually above the parent-child curves but below the
                event cards.  Pairs are deduped by sorted-id key so the line
                draws only once even if both events list each other. */}
            {(() => {
              const drawn = new Set();
              const lines = [];
              visIds.forEach(id => {
                const spouses = nm[id]?.ev?.spouseEventIds;
                if (!Array.isArray(spouses)) return;
                spouses.forEach(sid => {
                  if (!sid || sid === id) return;
                  if (!visIds.has(sid)) return; // partner not on screen
                  const key = id < sid ? `${id}|${sid}` : `${sid}|${id}`;
                  if (drawn.has(key)) return;
                  drawn.add(key);
                  // Order so left node is "a", right node is "b"
                  let aId = id, bId = sid;
                  if (positions[aId].x > positions[bId].x) { aId = sid; bId = id; }
                  const a = positions[aId];
                  const b = positions[bId];
                  // Node radii — match the logic at the node-draw site
                  // (spouse-only nodes are leaf-sized, not root-sized).
                  const radiusFor = (nodeId) => {
                    const n = nm[nodeId];
                    if (!n) return NR.branch;
                    const isSpouseOnly = spouseOnlySet.has(nodeId);
                    const isRoot = !n.ev.parentEventId && !isSpouseOnly;
                    const isLeaf = n.children.length === 0;
                    return isRoot ? NR.root : isLeaf ? NR.leaf : NR.branch;
                  };
                  const ra = radiusFor(aId);
                  const rb = radiusFor(bId);
                  // Connect right-edge of left node → left-edge of right node.
                  // Slight diagonal if they're not at the same y.
                  const x1 = a.x + ra;
                  const y1 = a.y;
                  const x2 = b.x - rb;
                  const y2 = b.y;
                  const mx = (x1 + x2) / 2;
                  const my = (y1 + y2) / 2;
                  lines.push(
                    <g key={`sp|${key}`} className="tree-spouse" data-pair={key}>
                      <line
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke="#C9A96E"
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        strokeLinecap="round"
                        opacity={0.7}
                        className="tree-spouse-line"
                      />
                      {/* Heart icon centered on midpoint. 12×12 by default,
                          CSS scales to 16×16 on hover via .tree-spouse:hover. */}
                      <g transform={`translate(${mx - 6} ${my - 6})`} className="tree-spouse-heart">
                        <path
                          d="M6 10.5 C 6 10.5 0.8 7.2 0.8 3.9 C 0.8 2.1 2.1 0.9 3.6 0.9 C 4.7 0.9 5.4 1.4 6 2.2 C 6.6 1.4 7.3 0.9 8.4 0.9 C 9.9 0.9 11.2 2.1 11.2 3.9 C 11.2 7.2 6 10.5 6 10.5 Z"
                          fill="#C9A96E"
                        />
                      </g>
                    </g>
                  );
                });
              });
              return lines;
            })()}

            {/* Nodes */}
            {[...visIds].map(id => {
              const pos  = positions[id];
              const node = nm[id];
              // Skip silently if the layout couldn't produce a finite point
              // for this node — e.g. its parent ended up in a cycle that
              // buildTree had to sever, or another branch threw mid-walk.
              // We prefer dropping the node to crashing the whole canvas.
              if (!pos || !node || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return null;
              const ev   = node.ev;
              const col  = branchColors[id] || colors.secondary;
              // Spouse-only nodes are sized like leaves (they sit beside a
              // spouse rather than anchoring a trunk), so they get NR.leaf
              // instead of the larger NR.root that a truly parentless node
              // would otherwise pick.
              const isSpouseOnly = spouseOnlySet.has(id);
              const isRoot    = !ev.parentEventId && !isSpouseOnly;
              const isLeaf    = node.children.length === 0;
              const isMilest  = ev.startDate === ev.endDate;
              const isCollaps = collapsed.has(id) && node.children.length > 0;
              const isSel     = selected === id;
              const isHov     = hovered === id;
              const isMatch   = matches.includes(id);
              const isCurMatch = curMatch === id;
              const hidden    = isCollaps ? countDesc(id, nm) : 0;
              const r         = isRoot ? NR.root : isLeaf ? NR.leaf : NR.branch;

              // Label alternation: even _pos → above/left, odd → below/right.
              // Spouse-only nodes override this so the label always sits on
              // the side AWAY from their partner — otherwise the label slab
              // crosses back over the partner's photo (e.g. Livia's label
              // landing on Augustus' bust).
              let side  = Math.round(node._p) % 2 === 0 ? -1 : 1;
              // Any node that has a visible spouse partner — root, spouse-
              // only, or otherwise — places its label on the side AWAY from
              // that partner. Spouse links are conceptually bidirectional
              // but the data may only store the connection on ONE side, so
              // we look both ways: this node's spouseEventIds AND any other
              // visible event that lists this node in its spouseEventIds.
              {
                let partner = (ev.spouseEventIds || []).find(s => positions[s] && visIds.has(s));
                if (!partner) {
                  for (const otherId of visIds) {
                    if (otherId === id) continue;
                    const otherSp = nm[otherId]?.ev?.spouseEventIds || [];
                    if (otherSp.includes(id) && positions[otherId]) { partner = otherId; break; }
                  }
                }
                if (partner && positions[partner]) {
                  const spp = positions[partner];
                  if (orient === 'lr') side = pos.y >= spp.y ? 1 : -1;
                  else                 side = pos.x >= spp.x ? 1 : -1;
                }
              }
              const label = ev.title.length > 20 ? ev.title.slice(0, 19) + '…' : ev.title;
              const dateStr = formatDate(ev.startDate, ev.startEra !== 'NONE');

              // Toggle button position (right of node for LR, below for TB)
              const tbx = orient === 'lr' ? pos.x + r + 10 : pos.x;
              const tby = orient === 'lr' ? pos.y          : pos.y + r + 10;

              // Label position
              const lx = orient === 'lr' ? pos.x : pos.x + side * (r + 16);
              const ly = orient === 'lr' ? pos.y + side * (r + 18) : pos.y;
              const anchor = orient === 'lr' ? 'middle' : side > 0 ? 'start' : 'end';
              const labelW = Math.min(160, label.length * 6.5 + 16);

              return (
                <g key={id}>
                  {/* Selection pulse ring */}
                  {isSel && (
                    <circle cx={pos.x} cy={pos.y} r={r + 8} fill="none"
                      stroke={colors.primary} strokeWidth={2} className="tree-sel-ring" />
                  )}

                  {/* Search highlight */}
                  {isMatch && (
                    <circle cx={pos.x} cy={pos.y} r={r + 5}
                      fill={isCurMatch ? `${colors.primary}30` : `${colors.secondary}22`}
                      stroke={isCurMatch ? colors.primary : colors.secondary}
                      strokeWidth={1.5} />
                  )}

                  {/* Node shape */}
                  {isMilest ? (
                    <rect
                      x={pos.x - MS_H} y={pos.y - MS_H}
                      width={MS_H * 2} height={MS_H * 2}
                      rx={2} fill={col} stroke="var(--bg)" strokeWidth={2}
                      transform={`rotate(45,${pos.x},${pos.y})`}
                      style={{ cursor: 'pointer' }}
                      onClick={e => handleNodeClick(id, e)}
                      onMouseEnter={e => { cancelHoverHide(); setHovered(id); setHoverScrPos({ x: e.clientX, y: e.clientY }); }}
                      onMouseLeave={scheduleHoverHide}
                    />
                  ) : (
                    <>
                      <circle
                        cx={pos.x} cy={pos.y} r={r}
                        fill={ev.photo ? 'transparent' : col}
                        stroke={ev.photo ? col : (isRoot ? 'var(--bg)' : 'none')}
                        strokeWidth={isRoot ? 3 : 2}
                        style={{ cursor: 'pointer', filter: isHov ? 'brightness(1.18)' : 'none', transition: 'filter 0.12s' }}
                        onClick={e => handleNodeClick(id, e)}
                        onMouseEnter={e => { cancelHoverHide(); setHovered(id); setHoverScrPos({ x: e.clientX, y: e.clientY }); }}
                        onMouseLeave={scheduleHoverHide}
                      />
                      {ev.photo && (
                        <>
                          <defs>
                            <clipPath id={`tc-${id}`}>
                              <circle cx={pos.x} cy={pos.y} r={r - 2} />
                            </clipPath>
                          </defs>
                          <TreeNodeImage
                            src={ev.photo}
                            x={pos.x - r + 2} y={pos.y - r + 2}
                            size={(r - 2) * 2}
                            clipId={`tc-${id}`}
                          />
                          <circle cx={pos.x} cy={pos.y} r={r} fill="transparent" stroke={col} strokeWidth={2.5}
                            style={{ cursor: 'pointer' }}
                            onClick={e => handleNodeClick(id, e)}
                            onMouseEnter={e => { cancelHoverHide(); setHovered(id); setHoverScrPos({ x: e.clientX, y: e.clientY }); }}
                            onMouseLeave={scheduleHoverHide}
                          />
                        </>
                      )}
                    </>
                  )}

                  {/* Collapse/expand toggle */}
                  {node.children.length > 0 && (
                    <g onClick={e => toggleCollapse(id, e)} style={{ cursor: 'pointer' }} role="button" aria-label={isCollaps ? 'Expand' : 'Collapse'}>
                      <circle cx={tbx} cy={tby} r={9}
                        fill={isCollaps ? col : 'var(--surface)'}
                        stroke={col} strokeWidth={1.5} />
                      <text x={tbx} y={tby + 4} textAnchor="middle"
                        fill={isCollaps ? '#fff' : col}
                        fontSize={isCollaps && hidden > 9 ? 8 : 11}
                        fontWeight={700} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        {isCollaps ? (hidden > 99 ? '…' : `+${hidden}`) : '−'}
                      </text>
                    </g>
                  )}

                </g>
              );
            })}

            {/* Label pass — rendered AFTER every node circle/photo so a
                later node's photo can never paint over an earlier node's
                label slab.  This eliminates the visual overlap the user
                saw between Aurelia's name card and Caesar's bust photo. */}
            {[...visIds].map(id => {
              const pos  = positions[id];
              const node = nm[id];
              if (!pos || !node || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return null;
              const ev   = node.ev;
              const col  = branchColors[id] || colors.secondary;
              const isSpouseOnly = spouseOnlySet.has(id);
              // Label side mirrors the node-block computation so the rect
              // lands exactly where it would if rendered inline.
              let side = Math.round(node._p) % 2 === 0 ? -1 : 1;
              if (isSpouseOnly) {
                const spouseIds = ev.spouseEventIds || [];
                const sp = spouseIds.find(s => positions[s]);
                if (sp && positions[sp]) {
                  const spp = positions[sp];
                  if (orient === 'lr') side = pos.y >= spp.y ? 1 : -1;
                  else                 side = pos.x >= spp.x ? 1 : -1;
                }
              }
              const isRoot = !ev.parentEventId && !isSpouseOnly;
              const isLeaf = node.children.length === 0;
              const r = isRoot ? NR.root : isLeaf ? NR.leaf : NR.branch;
              const lx = orient === 'lr' ? pos.x : pos.x + side * (r + 16);
              const ly = orient === 'lr' ? pos.y + side * (r + 18) : pos.y;
              const label = ev.title.length > 20 ? ev.title.slice(0, 19) + '…' : ev.title;
              const dateStr = formatDate(ev.startDate, ev.startEra !== 'NONE');
              const labelW = Math.min(160, label.length * 6.5 + 16);
              return (
                <g key={`label-${id}`} style={{ pointerEvents: 'none' }}>
                  {orient === 'lr' ? (
                    <>
                      <rect
                        x={lx - labelW / 2} y={ly + (side > 0 ? 2 : -28)}
                        width={labelW} height={26}
                        rx={4} fill="var(--surface)" stroke="var(--border)" strokeWidth={0.5}
                      />
                      <text x={lx} y={ly + (side > 0 ? 13 : -14)} textAnchor="middle"
                        fill="var(--text)" fontSize={10} fontWeight={600}>{label}</text>
                      <text x={lx} y={ly + (side > 0 ? 23 : -5)} textAnchor="middle"
                        fill="var(--text3)" fontSize={8}>{dateStr}</text>
                    </>
                  ) : (
                    <>
                      <rect
                        x={lx + (side > 0 ? 4 : -labelW - 4)} y={ly - 13}
                        width={labelW} height={26}
                        rx={4} fill="var(--surface)" stroke="var(--border)" strokeWidth={0.5}
                      />
                      <text x={lx + (side > 0 ? labelW / 2 + 4 : -labelW / 2 - 4)} y={ly - 2}
                        textAnchor="middle" fill="var(--text)" fontSize={10} fontWeight={600}>{label}</text>
                      <text x={lx + (side > 0 ? labelW / 2 + 4 : -labelW / 2 - 4)} y={ly + 9}
                        textAnchor="middle" fill="var(--text3)" fontSize={8}>{dateStr}</text>
                    </>
                  )}

                  {ev.branchLabel && ev.parentEventId && nm[ev.parentEventId] && !ev.secondParentEventId && (() => {
                    const pp = positions[ev.parentEventId];
                    if (!pp || !Number.isFinite(pp.x)) return null;
                    const mx = pp.x * 0.3 + pos.x * 0.7;
                    const my = pp.y * 0.3 + pos.y * 0.7;
                    return (
                      <text x={mx} y={my - 8} textAnchor="middle"
                        fill={col} fontSize={9} fontStyle="italic" fontWeight={600}>
                        {ev.branchLabel}
                      </text>
                    );
                  })()}
                </g>
              );
            })}
          </svg>
        </div>
        </TreeErrorBoundary>
      </div>

      {/* ── Hover preview card ── */}
      {hovered && nm[hovered] && (() => {
        const ev = nm[hovered].ev;
        const col = branchColors[hovered] || colors.secondary;
        const desc = stripHtml(ev.description || '').slice(0, 110);
        const hasChildren = nm[hovered].children.length > 0;
        return (
          <div
            className="tree-preview"
            style={{
              // Small gap (4px) instead of 14px so the cursor's transit
              // between the node and the preview is short enough that the
              // 450ms hide timer can never fire mid-traversal.
              left: Math.min(hoverScrPos.x + 4, window.innerWidth - PREVIEW_W - 12),
              top:  hoverScrPos.y + 4,
            }}
            onMouseEnter={cancelHoverHide}
            onMouseLeave={scheduleHoverHide}
          >
            <div className="tree-preview-pip" style={{ background: col }} />
            <div className="tree-preview-title">{ev.title}</div>
            <div className="tree-preview-date">
              {formatDate(ev.startDate, ev.startEra !== 'NONE')}
              {ev.startDate !== ev.endDate && <> — {formatDate(ev.endDate, ev.endEra !== 'NONE')}</>}
            </div>
            {desc && <div className="tree-preview-desc">{desc}{(ev.description?.length || 0) > 110 ? '…' : ''}</div>}
            <div className="tree-preview-actions">
              <button
                className="tree-preview-btn"
                style={{ borderColor: colors.primary, color: colors.primary }}
                onClick={e => { e.stopPropagation(); onEventClick(ev); }}
              >
                View Details
              </button>
              {hasChildren && !isolated && (
                <button
                  className="tree-preview-btn"
                  style={{ borderColor: col, color: col }}
                  onClick={e => { e.stopPropagation(); setIsolated(hovered); setHovered(null); }}
                >
                  Isolate Branch
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Legend — solid line = parent/child, dashed gold + heart = spousal */}
      <div className={`tree-legend${legendCollapsed ? ' tree-legend-collapsed' : ''}`}>
        {legendCollapsed ? (
          <button
            type="button"
            className="tree-legend-toggle"
            onClick={() => setLegendCollapsed(false)}
            title="Show legend"
            aria-label="Show legend"
          >ℹ</button>
        ) : (
          <>
            <div className="tree-legend-header">
              <span className="tree-legend-title">Legend</span>
              <button
                type="button"
                className="tree-legend-toggle"
                onClick={() => setLegendCollapsed(true)}
                title="Collapse legend"
                aria-label="Collapse legend"
              >−</button>
            </div>
            <div className="tree-legend-body">
              <div className="tree-legend-item">
                <span className="tree-legend-sample" aria-hidden="true">
                  <svg viewBox="0 0 28 14">
                    <path d="M2 7 Q 14 7 26 7" stroke={colors?.secondary || '#A8A9AD'} strokeWidth="2" fill="none" strokeLinecap="round" />
                  </svg>
                </span>
                <span>Parent/child (single)</span>
              </div>
              <div className="tree-legend-item">
                <span className="tree-legend-sample" aria-hidden="true">
                  <svg viewBox="0 0 28 14">
                    <line x1="3"  y1="3" x2="14" y2="7" stroke={colors?.secondary || '#A8A9AD'} strokeWidth="2" strokeLinecap="round" />
                    <line x1="25" y1="3" x2="14" y2="7" stroke={colors?.secondary || '#A8A9AD'} strokeWidth="2" strokeLinecap="round" />
                    <line x1="14" y1="7" x2="14" y2="13" stroke={colors?.secondary || '#A8A9AD'} strokeWidth="2" strokeLinecap="round" />
                    <circle cx="14" cy="7" r="2" fill="#C9A96E" />
                  </svg>
                </span>
                <span>Parent/child (both parents)</span>
              </div>
              <div className="tree-legend-item">
                <span className="tree-legend-sample" aria-hidden="true">
                  <svg viewBox="0 0 28 14">
                    <line x1="2" y1="7" x2="26" y2="7" stroke="#C9A96E" strokeWidth="2" strokeDasharray="3 2" strokeLinecap="round" opacity="0.85" />
                    <path d="M14 9.5 C 14 9.5 11.4 7.8 11.4 6.2 C 11.4 5.3 12 4.7 12.7 4.7 C 13.3 4.7 13.7 5 14 5.4 C 14.3 5 14.7 4.7 15.3 4.7 C 16 4.7 16.6 5.3 16.6 6.2 C 16.6 7.8 14 9.5 14 9.5 Z" fill="#C9A96E" />
                  </svg>
                </span>
                <span>Spouse / partner</span>
              </div>
              <div className="tree-legend-item">
                <span className="tree-legend-sample" aria-hidden="true">
                  <svg viewBox="0 0 28 14">
                    <line x1="2" y1="7" x2="26" y2="7" stroke="#C9A96E" strokeWidth="2" strokeDasharray="1.5 3" strokeLinecap="round" opacity="0.75" />
                    <rect x="11.5" y="3.5" width="5" height="7" rx="1" fill="#C9A96E" />
                    <line x1="12.5" y1="5.5" x2="15.5" y2="5.5" stroke="#1A1408" strokeWidth="0.5" />
                    <line x1="12.5" y1="7"   x2="15.5" y2="7"   stroke="#1A1408" strokeWidth="0.5" />
                    <line x1="12.5" y1="8.5" x2="14"   y2="8.5" stroke="#1A1408" strokeWidth="0.5" />
                  </svg>
                </span>
                <span>Adoption</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Hint */}
      <div className="tree-hint">
        Ctrl+scroll to zoom · drag to pan · click node to preview · double-click to open
        · pinch to zoom · two-finger drag to pan · long-press branch to collapse
      </div>
    </div>
  );
};

export default TreeTimeline;
